import {authService} from './authService';
import {
    WS_RECONNECT_INITIAL_DELAY,
    WS_RECONNECT_MAX_ATTEMPTS,
    WS_RECONNECT_MAX_DELAY,
} from './config';

const AUTH_CLOSE_CODES = new Set([
    1008, // policy violation
    4001,
    4003,
    4401,
    4403,
]);

const buildUrlWithToken = (url, token) => {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}token=${encodeURIComponent(token)}`;
};

export class AuthenticatedWebSocket {
    constructor({url, name = 'WebSocket', onOpen, onMessage, onClose, onError}) {
        this.url = url;
        this.name = name;
        this.onOpen = onOpen;
        this.onMessage = onMessage;
        this.onClose = onClose;
        this.onError = onError;

        this.socket = null;
        this.reconnectTimer = null;
        this.reconnectAttempt = 0;
        this.isStopped = true;
    }

    async connect({forceRefresh = false} = {}) {
        this.isStopped = false;
        this.clearReconnectTimer();

        try {
            const token = await this.getValidToken(forceRefresh);

            if (!token || this.isStopped) {
                return;
            }

            this.openSocket(token);
        } catch (error) {
            console.error(`${this.name}: не удалось получить access token`, error);
            this.scheduleReconnect({forceRefresh: true});
        }
    }

    stop() {
        this.isStopped = true;
        this.clearReconnectTimer();

        if (this.socket) {
            this.socket.onopen = null;
            this.socket.onmessage = null;
            this.socket.onerror = null;
            this.socket.onclose = null;

            if (
                this.socket.readyState === WebSocket.OPEN ||
                this.socket.readyState === WebSocket.CONNECTING
            ) {
                this.socket.close(1000, 'Client stopped');
            }
        }

        this.socket = null;
    }

    send(data) {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            return false;
        }

        this.socket.send(typeof data === 'string' ? data : JSON.stringify(data));
        return true;
    }

    async getValidToken(forceRefresh = false) {
        const currentToken = authService.getAccessToken();

        if (!forceRefresh && currentToken && !authService.isTokenExpired(30)) {
            return currentToken;
        }

        const data = await authService.refreshAccessToken();
        return data?.access_token || authService.getAccessToken();
    }

    openSocket(token) {
        if (this.socket) {
            this.socket.onclose = null;
            this.socket.close();
        }

        this.socket = new WebSocket(buildUrlWithToken(this.url, token));

        this.socket.onopen = (event) => {
            this.reconnectAttempt = 0;
            console.log(`${this.name}: соединение установлено`);
            this.onOpen?.(event, this);
        };

        this.socket.onmessage = (event) => {
            this.onMessage?.(event, this);
        };

        this.socket.onerror = (event) => {
            console.error(`${this.name}: ошибка соединения`, event);
            this.onError?.(event, this);
        };

        this.socket.onclose = (event) => {
            console.log(`${this.name}: соединение закрыто`, event.code, event.reason);
            this.onClose?.(event, this);

            if (this.isStopped) {
                return;
            }

            this.scheduleReconnect({
                forceRefresh: AUTH_CLOSE_CODES.has(event.code),
            });
        };
    }

    scheduleReconnect({forceRefresh = false} = {}) {
        if (this.isStopped) {
            return;
        }

        if (this.reconnectAttempt >= WS_RECONNECT_MAX_ATTEMPTS) {
            console.error(`${this.name}: превышен лимит переподключений`);
            this.stop();
            return;
        }

        const exponentialDelay = WS_RECONNECT_INITIAL_DELAY * (2 ** this.reconnectAttempt);
        const delay = Math.min(exponentialDelay, WS_RECONNECT_MAX_DELAY);
        const jitter = Math.floor(Math.random() * 500);

        this.reconnectAttempt += 1;
        this.clearReconnectTimer();

        this.reconnectTimer = setTimeout(() => {
            this.connect({forceRefresh});
        }, delay + jitter);
    }

    clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
}
